<script setup>

import BaseDropdown from "@/Components/UI/dropdown/BaseDropdown.vue";
import BaseDropdownOption from "@/Components/UI/dropdown/BaseDropdownOption.vue";
import {router, usePage} from "@inertiajs/vue3";
import {computed, nextTick, ref} from "vue";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";
import TeamIcon from "@/Components/UI/nav/TeamIcon.vue";
import BaseModal from "@/Components/UI/modals/BaseModal.vue";
import OrganizationForm from "@/Components/organization/OrganizationForm.vue";

defineOptions({
    inheritAttrs: false
});

const createPersonalTeam = () => {
    router.post(route('teams.personal.create'))
}

const switchTeam = (team) => {
    router.put(route('teams.switch', team.id));
}

const dropdown = ref(null);

const isCurrentTeam = (team) => {
    return team.id === usePage().props.auth.teams.current?.id;
}

const noPersonalTeam = computed(() => {
    return !usePage().props.auth.teams.all.some(team => team.personal);
});

const shouldHidePersonalButton = computed(() => {
    return !usePage().props.external_edo_enabled;
});

const hasMultipleTeams = computed(() => {
    return usePage().props.auth.teams.all.length > 1;
});

const shouldHideSwitcher = computed(() => {
    return !usePage().props.external_edo_enabled && !hasMultipleTeams.value;
});

const createOrganizationModal = ref(false);
const createOrganizationForm = ref(null);
const createOrganization = async () => {
    // PrimeVue Dialog renders the form slot only when visible, so show first, then fill.
    createOrganizationModal.value = true;
    await nextTick();
    createOrganizationForm.value.fillData({});
}
</script>

<template>
    <div>
        <BaseModal max-width="2xl"
                   :show="createOrganizationModal"
                   title="Организация"
                   @close="createOrganizationModal = false">
            <OrganizationForm ref="createOrganizationForm"/>
        </BaseModal>

        <template v-if="!shouldHideSwitcher">
        <BaseDropdown ref="dropdown">
        <template #trigger>
            <BaseButton variant="secondary"
                                 class="w-full justify-start hover:border-light-2 hover:text-dark-light hover:stroke-dark-2 hover:fill-dark-2"
                                 v-tooltip.bottom="'Переключить активную организацию'"
                                 v-bind="$attrs">
                <div class="flex items-center justify-center stroke-dark-light">
                    <TeamIcon :personal="Boolean($page.props.auth?.teams?.current?.personal)"/>
                </div>
                <span class="text-dark-light text-ellipsis overflow-hidden whitespace-nowrap md:max-w-72">
                    {{ $page.props.auth.teams.current?.name }}
                </span>
                <div class="ml-auto">
                    <svg class="fill-dark-light"
                         height="7"
                         viewBox="0 0 12 7"
                         width="12"
                         stroke="none"
                         xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M11.3729 1.19584C11.3735 1.29691 11.3541 1.39711 11.3159 1.49068C11.2777 1.58425 11.2214 1.66936 11.1502 1.74112L6.54212 6.34918C6.39823 6.49222 6.20357 6.57251 6.00068 6.57251C5.79778 6.57251 5.60313 6.49222 5.45923 6.34918L0.851177 1.74112C0.725357 1.5942 0.659611 1.40522 0.667076 1.21193C0.674542 1.01864 0.75467 0.835282 0.891448 0.698504C1.02823 0.561726 1.21158 0.481597 1.40487 0.474132C1.59816 0.466666 1.78715 0.532412 1.93407 0.658232L5.99684 4.721L10.0596 0.658232C10.1666 0.550387 10.3031 0.47668 10.452 0.446432C10.6008 0.416184 10.7553 0.430753 10.8959 0.488297C11.0364 0.54584 11.1568 0.643774 11.2417 0.769714C11.3266 0.895655 11.3723 1.04395 11.3729 1.19584Z"/>
                    </svg>
                </div>
            </BaseButton>
        </template>

        <template #content>
            <template v-for="team in $page.props.auth.teams.all"
                      :key="team.id">
                <BaseDropdownOption :active="isCurrentTeam(team)"
                                    class="dropdown-option"
                                    @click="switchTeam(team)">
                    <TeamIcon :personal="Boolean(team.personal)"/>
                    <span>
                        {{ team.name }}
                    </span>
                </BaseDropdownOption>
            </template>
            <!-- Actions are separated from the team rows by the same rule the KB
                 space picker uses, so the panel reads as list-then-actions rather
                 than one undifferentiated stack. -->
            <div class="border-t border-light-2 mt-4 pt-4 flex flex-col gap-4">
                <BaseButton variant="primary" class="w-full" v-if="noPersonalTeam && !shouldHidePersonalButton"
                                   @click="createPersonalTeam">
                    Войти как физ. лицо
                </BaseButton>

                <BaseButton variant="primary" class="w-full" @click="createOrganization">
                    Добавить организацию
                </BaseButton>
            </div>
            </template>
        </BaseDropdown>
        </template>
    </div>
</template>

<style scoped>
.dropdown-option {
    @apply min-w-128 max-w-xl text-wrap text-left text-base
}
</style>
